import React from "react"
import styled from "styled-components"
import moment from "moment"
import {Container, Row, Col} from "@uprise/grid"
import {TextInputHorizontal} from "@uprise/form"
import {H4, H6} from "@uprise/headings"
import {P} from "@uprise/text"
import {Button} from "@uprise/button"
import {useForm, Controller} from "react-hook-form"
import {extended, semantic} from "@uprise/colors"

import StatusButton from "./StatusButton"

const FormElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormElementLabel = styled(H6)`
	margin: 16px 0 8px 0;

	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormInputContainer = styled.div`
	flex-basis: 60%;
	position: relative;
	align-self: center;
`

const FormTextInputWithBorder = styled(TextInputHorizontal)`
	border-radius: 4px;
	border: solid 1px #d4cbfb;
	padding: 4px 10px;
`

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
	outline: none;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
	padding-left: 40%;
	text-align: left;
`

const MessageStylesCaps = styled(MessageStyles)`
	padding-left: 40%;
	text-align: left;
`

const ReadOnlyField = styled.div`
	color: #6d6c79;
	text-align: left;
`

const ViewRequest = ({onCancel, data, onComplete}) => {
	const {register, control, handleSubmit, errors} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		mode: "onSubmit",
		reValidateMode: "onChange"
	})

	const {
		userName,
		userEmail,
		approverName,
		approverEmail,
		companyName,
		createdAt,
		updatedAt,
		invoice,
		remarks,
		sessions,
		code,
		status,
		id
	} = data

	const validateText = (text, field) => {
		return !text.trim() ? `${field} can not be left empty` : true
	}

	const disableInvoice = status !== "approved"

	return (
		<Container>
			<form
				onSubmit={handleSubmit(formData => {
					onComplete({...formData, id})
				})}>
				<Row>
					{/* =================== Requestor ====================== */}
					<Col>
						<H4 style={{textAlign: "left"}} className='m-t-10 m-b-5'>
							Request Details
						</H4>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Requestor's Name</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{userName}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Requestor's Email</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{userEmail}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Company Name</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{companyName}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Employer's Code</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{code}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Date Requested</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{moment(createdAt).format("DD-MMM-YYYY")}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>No. of Sessions</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{sessions}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Request Channel</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField></ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
				</Row>

				<Row>
					{/* =================== Approval ====================== */}
					<Col>
						<H4 style={{textAlign: "left"}} className='m-t-10 m-b-5'>
							Approval Status
						</H4>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Approver POC</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{approverName}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Status</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>
									<Col className='col-8 col-lg-8 col-md-8 col-sm-12'>
										<StatusButton status={status} />
									</Col>
								</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Date</FormElementLabel>
							<FormInputContainer>
								<ReadOnlyField>{moment(updatedAt).format("DD-MMM-YYYY")}</ReadOnlyField>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
				</Row>

				<Row>
					{/* =================== Approval ====================== */}
					<Col>
						<H4 style={{textAlign: "left"}} className='m-t-10 m-b-5'>
							Billing Details
						</H4>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel isRequired={disableInvoice}>Invoice Details</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"invoice"}
									defaultValue={invoice || ""}
									control={control}
									rules={{
										required: {
											value: !disableInvoice,
											message: "Invoice can not be left empty"
										},
										validate: text => {
											if (disableInvoice) return true
											else validateText(text, "Invoice")
										},
										maxLength: {
											value: 500,
											message: "Invoice can not exceed 500 characters"
										}
									}}
									as={
										<FormTextInputWithBorder
											type='text'
											ref={register({required: true})}
											disabled={disableInvoice}
										/>
									}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["invoice"] && errors["invoice"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["invoice"]}>
								{errors["invoice"] && errors["invoice"].message}
							</MessageStyles>
						) : null}
					</Col>

					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel isRequired={true}>Remarks</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"remarks"}
									defaultValue={remarks || ""}
									rules={{
										required: {
											value: true,
											message: "Remarks can not be left empty"
										},
										validate: text => validateText(text, "Remarks"),
										maxLength: {
											value: 500,
											message: "Remarks can not exceed 500 characters"
										}
									}}
									control={control}
									as={<FormTextInputWithBorder type='text' ref={register({required: true})} />}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["remarks"] && errors["remarks"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["remarks"]}>
								{errors["remarks"] && errors["remarks"].message}
							</MessageStyles>
						) : null}
					</Col>
				</Row>

				<Row className='flex-row-reverse'>
					<ButtonContainer>
						<PrimaryButton type='submit' variant={"primary"} title={"Save"} />
					</ButtonContainer>
				</Row>
			</form>
		</Container>
	)
}

export default ViewRequest
